import i18n from '../i18n';
import { getSpinnerFrame } from './spinner';

const getApiBaseUrl = (): string => {
    if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
    const { hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || /^192\.168\./.test(hostname) || /^10\./.test(hostname) || /^100\./.test(hostname)) {
        return `http://${hostname}:5000`;
    }
    return "https://www.nodecharm.com";
};

const API_BASE = getApiBaseUrl();

const isMobile = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

const constants = {
    fetchNodeChat: API_BASE + "/api/node/chat",
    fetchNodeContent: API_BASE + "/api/node/content",
    fetchNodeFlashcards: API_BASE + "/api/node/flashcards",

    fetchGraphGameUrl: API_BASE + "/api/game",
    fetchGraphGameAnswerUrl: API_BASE + "/api/game?answer=true",
    fetchGraphGameHintUrl: API_BASE + "/api/game/hint",

    buildGraphUrl: API_BASE + "/api/graph/build",
    improveGraphUrl: API_BASE + "/api/graph/improve",
    fileExtractUrl: API_BASE + "/api/graph/file/extract",
    organizeGraphUrl: API_BASE + "/api/graph/organize",

    fetchLogin: API_BASE + "/api/auth/login",
    fetchAuthRefresh: API_BASE + "/api/auth/refresh",
    fetchRegister: API_BASE + "/api/auth/register",
    fetchPasswordRecovery: API_BASE + "/api/auth/password-recovery",
    fetchResetPassword: API_BASE + "/api/auth/reset-password",
    fetchAuthLogout: API_BASE + "/api/auth/logout",

    fetchPaymentCreateCheckout: API_BASE + "/api/payment/checkout-session",
    fetchChatById: API_BASE + "/api/chat",
    fetchGameState: API_BASE + "/api/chat",
    fetchGameStateContext: API_BASE + "/api/chat",
    fetchFileList: API_BASE + "/api/file/list",
    fetchFileEdit: API_BASE + "/api/file/edit",
    fetchEmbedding: API_BASE + "/api/file/embedding",
    reportError: API_BASE + "/api/errors",
    fetchSearchQuery: API_BASE + "/api/search/query",
    fetchFileEmbed: API_BASE + "/api/file/embed",

    splitStringEveryNChars: 40 as number,
    baseIntervalForWordHighlight: 60 as number,
    hintTime: 20 as number,
    answerTime: 30 as number,
    graph: {
        options: {
            nodes: {
                color: {
                    border: '#858585',
                    background: '#191919',
                    hover: {
                        border: '#5BD75B',
                        background: '#5BD75B',
                    },
                    highlight: {
                        border: '#5BD75B',
                        background: '#5BD75B',
                    },
                },
                shape: 'dot',
                font: {
                    size: 14,
                    color: '#929292',
                    face: 'Arial',
                    multi: true
                },
                scaling: {
                    min: 20,
                    max: 150,
                },
            },
            layout: {
                hierarchical: false
            },
            edges: {
                color: {
                    color: "#5E5E5E",
                    hover: "#5BD75B",
                    highlight: "#5BD75B",
                },
                arrows: {
                    to: { enabled: true, scaleFactor: 1 }
                },
                smooth: {
                    type: 'continuous',
                    forceDirection: 'none',
                    roundness: 0.5,
                },
                font: {
                    color: "#A0A0A0",
                    size: 14,
                    face: "arial",
                    background: "none",
                    strokeWidth: 0,
                    strokeColor: "#ffffff"
                },
                width: 1,
                hoverWidth: 1,
                selectionWidth: 1,
            },
            height: "100vh",
            interaction: {
                tooltipDelay: 99999999999999999999,
                hover: !isMobile,
                multiselect: true,
                selectConnectedEdges: false
            },
            physics: {
                enabled: true,
                solver: 'forceAtlas2Based',
                forceAtlas2Based: {
                    gravitationalConstant: -1000,
                    centralGravity: 0.01,
                    springLength: 200,
                    springConstant: 0.08,
                    damping: 0.9
                },
                maxVelocity: 15,
                timestep: 0.3,
                stabilization: {
                    enabled: true,
                    iterations: 30,
                    updateInterval: 25,
                },
            },
            groups: {
                loading: {
                    shape: 'custom',
                    ctxRenderer: ({ ctx, x, y }: { ctx: CanvasRenderingContext2D; x: number; y: number; id: number | string }) => {
                        const spinner = getSpinnerFrame();
                        ctx.font = '22px Arial';
                        ctx.fillStyle = '#ffffff77';
                        ctx.textAlign = 'center';
                        ctx.textBaseline = 'middle';
                        // @ts-ignore - i18n.t typing issue from .js module
                        ctx.fillText(`${spinner} ${i18n.t('common.loading')}`, x, y);
                        return true;
                    },
                },
                default: {
                    shape: 'dot',
                    font: {
                        size: 14,
                        color: '#ffffff77',
                        face: 'Arial',
                        multi: true
                    },
                },
                correct: {
                    shape: 'dot',
                    size: 14,
                    color: {
                        border: '#5BD75B',
                        background: '#191919'
                    }
                },
                selected: {
                    shape: 'dot',
                    size: 55,
                    color: {
                        border: '#858585',
                        background: '#191919'
                    }
                },
                active: {
                    shape: 'dot',
                    size: 14,
                    color: {
                        border: '#474747',
                        background: '#858585'
                    }
                },
                page: {
                    shape: 'dot',
                    size: 55,
                    color: {
                        border: '#858585',
                        background: '#191919'
                    }
                },
                chapter: {
                    shape: 'dot',
                    size: 60,
                    color: {
                        border: '#6EA6E1',
                        background: '#191919'
                    }
                },
                done: {
                    shape: 'dot',
                    size: 14,
                    color: {
                        border: '#5BD75B',
                        background: '#191919',
                        borderWidth: 3
                    }
                }
            }
        },

        nodes: {
            style: {
                selected: {
                    font: {
                        color: '#ffffff',
                        strokeWidth: 1,
                        strokeColor: "#000000"
                    },
                }
            }
        },
        edges: {
            style: {
                normal: {
                    color: '#ffffff2f'
                },
                selected: {
                    color: "#9FF084"
                },
                from: {
                    color: '#696969'
                },
                to: {
                    color: '#33CCFF7f'
                }
            }
        }
    }
} as const;

export default constants;
